import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { Avatar } from '../components/Avatar';
import { api } from '../lib/api';

interface Data {
  id: string;
  name: string;
  avatar: string | null;
  isGuest: boolean;
  gamesPlayed: number;
  wins: number;
  losses: number;
  draws: number;
  currentWinStreak: number;
  longestWinStreak: number;
  winRate: number;
  h2h: { gamesPlayed: number; wins: number; losses: number; draws: number } | null;
  recent: Array<{ gameId: string; finishOrder: number; isDurak: boolean; startedAt: string; endedAt: string | null }>;
}

export default function PublicStatsPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<Data | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    // Reset when navigating player→player so the previous profile doesn't
    // linger while the new one loads, and ignore out-of-date responses.
    setData(null);
    setErr(null);
    let cancelled = false;
    api.userStats(id)
      .then((d) => { if (!cancelled) setData(d as any); })
      .catch((e) => { if (!cancelled) setErr(e.message); });
    return () => { cancelled = true; };
  }, [id]);

  return (
    <AppShell>
      {err && <div className="text-rose-400 mb-3">{err}</div>}
      {!data ? (
        <div className="text-ink-200 animate-pulse">Loading…</div>
      ) : (
        <div className="space-y-4">
          <header className="flex items-center gap-3">
            <Avatar src={data.avatar} name={data.name} size={48} />
            <div className="min-w-0">
              <h1 className="font-display text-xl font-bold truncate">{data.name}</h1>
              <div className="text-xs text-ink-200/80">{data.isGuest ? 'Guest player' : 'Player profile'}</div>
            </div>
          </header>

          {data.h2h && (
            <section className="panel p-4">
              <h2 className="section-label mb-2">Head-to-head vs you</h2>
              {data.h2h.gamesPlayed === 0 ? (
                <div className="text-ink-300 text-sm italic">You haven't played together yet.</div>
              ) : (
                <div className="flex items-end gap-6">
                  <div>
                    <div className="text-3xl font-display font-bold text-emerald-400">{data.h2h.wins}</div>
                    <div className="text-[10px] uppercase tracking-wider text-ink-200/80">your wins</div>
                  </div>
                  <div>
                    <div className="text-3xl font-display font-bold text-rose-400">{data.h2h.losses}</div>
                    <div className="text-[10px] uppercase tracking-wider text-ink-200/80">your losses</div>
                  </div>
                  {data.h2h.draws > 0 && (
                    <div>
                      <div className="text-3xl font-display font-bold text-ink-100">{data.h2h.draws}</div>
                      <div className="text-[10px] uppercase tracking-wider text-ink-200/80">draws</div>
                    </div>
                  )}
                  <div className="ml-auto text-right">
                    <div className="text-2xl font-display font-bold text-accent">
                      {Math.round((data.h2h.wins / Math.max(1, data.h2h.gamesPlayed)) * 100)}%
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-ink-200/80">your win rate</div>
                  </div>
                </div>
              )}
            </section>
          )}

          <section>
            <h2 className="section-label mb-2">Career</h2>
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
              <Stat label="Games" value={data.gamesPlayed} />
              <Stat label="Wins" value={data.wins} accent />
              <Stat label="Losses (Durak)" value={data.losses} />
              <Stat label="Win rate" value={`${Math.round(data.winRate * 100)}%`} />
              <Stat label="Draws" value={data.draws} />
              <Stat label="Current streak" value={data.currentWinStreak} />
              <Stat label="Longest streak" value={data.longestWinStreak} accent />
            </div>
          </section>

          <section className="panel p-4">
            <h2 className="section-label mb-2">{data.h2h ? 'Recent games together' : 'Recent games'}</h2>
            {data.recent.length === 0 ? (
              <div className="text-ink-300 text-sm">{data.h2h ? "You haven't played together yet." : 'No games played yet.'}</div>
            ) : (
              <ul className="divide-y divide-ink-700/60">
                {data.recent.map((g) => (
                  <li key={g.gameId} className="py-2 flex items-center justify-between text-sm">
                    <div>{new Date(g.endedAt || g.startedAt).toLocaleString()}</div>
                    <div className={g.isDurak ? 'text-rose-400' : g.finishOrder === 0 ? 'text-emerald-400' : 'text-ink-100'}>
                      {g.isDurak ? 'Durak' : g.finishOrder === 0 ? '1st out' : `${ordinal(g.finishOrder + 1)} out`}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </AppShell>
  );
}

function ordinal(n: number) {
  const s = ['th','st','nd','rd'], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function Stat({ label, value, accent }: { label: string; value: string | number; accent?: boolean }) {
  return (
    <div className="panel p-4">
      <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-200">{label}</div>
      <div className={`text-2xl font-display font-bold mt-1 ${accent ? 'text-accent' : 'text-ink-50'}`}>{value}</div>
    </div>
  );
}
